import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import cn from "classnames";
import AiDisclaimer from "../AiDisclaimer";
import { PlayIcon } from "@heroicons/react/24/solid";
import { InvestigationEntity, InvestigationEntityMention } from "../../../db/schema";
import Button from "../../common/Button";
import Dropdown from "../../Settings/Dropdown";
import { api, EntityListItem, formatMs } from "../api";
import { clearingClass, isNewSince, UpdatesBanner } from "../updates";
import { useNowPlaying } from "../NowPlayingContext";
import AnalyzingState from "../AnalyzingState";

const TYPE_LABELS: Record<string, string> = { person: "People", phone: "Phone numbers", address: "Addresses", org: "Organizations", vehicle: "Vehicles", money_account: "Accounts", alias: "Aliases", other: "Other" };

const PeopleTab: React.FC<{ investigationId: string; reanalyzing?: boolean; updates?: number; since?: string; onSeen?: () => void; clearing?: boolean }> = ({ investigationId, reanalyzing, updates = 0, since, onSeen, clearing }) => {
  const navigate = useNavigate();
  const { play } = useNowPlaying();
  const [params, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [entities, setEntities] = useState<EntityListItem[]>([]);
  const [selected, setSelected] = useState<(InvestigationEntity & { mentions: (InvestigationEntityMention & { fileName: string })[] }) | null>(null);
  const [mergeTarget, setMergeTarget] = useState("");
  const [search, setSearch] = useState("");

  const load = async () => setEntities(await api.entities(investigationId));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investigationId]);
  useEffect(() => {
    const id = params.get("entity");
    if (id && id !== selected?.id) select(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const detailRef = useRef<HTMLDivElement>(null);
  const select = async (id: string) => {
    try {
      setSelected(await api.entity(investigationId, id));
      detailRef.current?.scrollTo({ top: 0 });
      // Keep router state (the "back to call" marker) across the replace.
      if (params.get("entity") !== id) setSearchParams({ entity: id }, { replace: true, state: location.state });
    } catch {
      toast.error("Failed to load");
    }
  };

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q ? entities.filter((e) => e.canonical_name.toLowerCase().includes(q) || (e.aliases || []).some((a) => a.toLowerCase().includes(q))) : entities;
    const g: Record<string, EntityListItem[]> = {};
    for (const e of list) (g[e.type] ||= []).push(e);
    return g;
  }, [entities, search]);

  const merge = async () => {
    if (!selected || !mergeTarget) return;
    await api.mergeEntity(investigationId, selected.id, mergeTarget);
    toast.success("Merged");
    setMergeTarget("");
    await load();
    select(mergeTarget);
  };

  const rename = async () => {
    if (!selected) return;
    const name = window.prompt("Canonical name", selected.canonical_name);
    if (!name || name === selected.canonical_name) return;
    try {
      await api.updateEntity(investigationId, selected.id, { canonical_name: name });
      await load();
      select(selected.id);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Failed to rename");
    }
  };

  if (reanalyzing) {
    return <AnalyzingState title="Re-analyzing every call" body="People, numbers, and places are extracted again from each call. Your renames and merges are kept." />;
  }

  const entityIsNew = (e: EntityListItem) => isNewSince(e.created_at, since) || isNewSince(e.last_mention_at, since);

  return (
    <div>
    {onSeen && <UpdatesBanner tab="people" count={updates} since={since} onDismiss={onSeen} clearing={clearing} className="mb-4" />}
    {entities.length > 0 && <AiDisclaimer kind="entities" className="mb-4" />}
    <div className="flex gap-6">
      <div className="w-80 shrink-0">
        <input className="input input-sm input-bordered bg-base-100 w-full mb-3" placeholder="Search people, numbers..." value={search} onChange={(e) => setSearch(e.target.value)} />
        {entities.length === 0 && <p className="text-sm text-base-content/60">No entities yet. They're extracted as calls are analyzed.</p>}
        {Object.entries(grouped).map(([type, list]) => (
          <div key={type} className="mb-3">
            <p className="text-xs font-semibold uppercase text-base-content/50 mb-1">{TYPE_LABELS[type] || type}</p>
            <ul>
              {list.map((e) => (
                <li key={e.id}>
                  <button onClick={() => select(e.id)} className={cn("w-full text-left px-2 py-1 rounded text-sm flex items-center justify-between gap-2 transition-[background-color] duration-[650ms] ease-out", selected?.id === e.id ? "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300" : entityIsNew(e) && !clearing ? "bg-blue-50/60 dark:bg-blue-900/20 hover:bg-base-200" : "hover:bg-base-200")}>
                    <span className="truncate flex items-center gap-1.5">
                      {entityIsNew(e) && (
                        <span className={cn("grid shrink-0 transition-[grid-template-columns,margin] duration-[650ms] ease-out", clearing ? "grid-cols-[0fr] -mr-1.5" : "grid-cols-[1fr]")} aria-hidden>
                          <span className="min-w-0 overflow-hidden">
                            <span className={cn("block size-1.5 rounded-full bg-blue-400 dark:bg-blue-300", clearingClass(clearing))} />
                          </span>
                        </span>
                      )}
                      {e.canonical_name}
                    </span>
                    <span className="text-xs text-base-content/50">{e.mention_count}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      {/* Docked: sticks to the top of the pane while the (long) entity list
          scrolls, and scrolls on its own if the mentions outgrow the viewport,
          so a pick far down the list is visible immediately. */}
      <div ref={detailRef} className="flex-1 min-w-0 sticky top-6 self-start max-h-[calc(100vh-12.5rem)] overflow-y-auto">
        {!selected ? (
          <p className="text-sm text-base-content/60">Select a person or number to see every mention across the investigation.</p>
        ) : (
          <div>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h2 className="text-lg font-bold text-base-content">{selected.canonical_name} <span className="text-sm font-normal text-base-content/50">{selected.type}</span></h2>
                {selected.aliases && selected.aliases.length > 0 && <p className="text-sm text-base-content/70">Also: {selected.aliases.join(", ")}</p>}
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={rename}>Rename</Button>
                <Dropdown
                  size="sm"
                  className="w-56 max-w-full"
                  contentClassName="w-64"
                  options={[{ value: "", label: "Merge into…" }, ...entities.filter((e) => e.id !== selected.id).map((e) => ({ value: e.id, label: `${e.canonical_name} (${e.type})` }))]}
                  value={mergeTarget}
                  onChange={setMergeTarget}
                />
                <Button size="sm" variant="primary" disabled={!mergeTarget} onClick={merge}>Merge</Button>
              </div>
            </div>
            <textarea
              className="textarea textarea-bordered bg-base-100 w-full mt-3 text-sm"
              rows={2}
              placeholder="Notes about this person / number"
              defaultValue={selected.notes || ""}
              onBlur={(e) => e.target.value !== (selected.notes || "") && api.updateEntity(investigationId, selected.id, { notes: e.target.value })}
            />
            <h3 className="font-semibold text-base-content mt-4 mb-2">{selected.mentions.length} mention{selected.mentions.length === 1 ? "" : "s"}</h3>
            <ul className="space-y-2">
              {selected.mentions.map((m) => (
                <li key={m.id} className="rounded-lg border border-base-300 bg-base-100 p-3 text-sm">
                  <div className="flex items-center gap-2 text-xs text-base-content/60">
                    {m.call_id ? (
                      <button className="truncate hover:underline hover:text-base-content" title="Open call" onClick={() => navigate(`/investigations/${investigationId}/calls/${m.call_id}?t=${m.start_ms || 0}`)}>
                        {m.fileName}
                      </button>
                    ) : (
                      <button
                        className="truncate hover:underline hover:text-base-content"
                        title="Open file"
                        onClick={() => navigate(`/investigations/${investigationId}/files/${m.file_id}${m.page ? `?page=${m.page}` : ""}`)}
                      >
                        {m.fileName}
                        {m.page ? ` · p. ${m.page}` : ""}
                      </button>
                    )}
                    {m.call_id && (
                      <button className="ml-auto inline-flex items-center gap-1 text-blue-600 dark:text-blue-300 hover:underline" title="Play in the mini player" onClick={() => play(investigationId, m.call_id!, m.start_ms || 0)}>
                        <PlayIcon className="size-3" /> {formatMs(m.start_ms)}
                      </button>
                    )}
                  </div>
                  <p className="mt-1 text-base-content/90">"{m.surface_form}" <span className="text-base-content/60">— {m.context_snippet}</span></p>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
    </div>
  );
};

export default PeopleTab;
